import { useEffect, useState } from "react";

import { log } from "utils/log";

import { fetchPreviewBitmap } from "./fetchPreviewBitmap";
import { type TiledDrawingSource } from "./tileMath";

const l = log.miniMap.tiled;

/**
 * Mini map bitmap for tiled drawings (consumed inside `useDrawingImage`):
 * fetches and decodes the preview tier fitting the consumer's `maxSize` cap
 * (web 768 → `small` 512 px, mobile 1536 → `medium` 1536 px). Ownership
 * mirrors the PDF hook: the bitmap is closed on source/maxSize change or
 * unmount and never handed out detached.
 *
 * Returns `undefined` while loading, without a source, or without `maxSize`
 * (the large maps paint tiles per viewport instead — `useTiledLargeMap`).
 */
export function useTiledDrawingImage(
    source: TiledDrawingSource | undefined,
    maxSize: number | undefined,
): ImageBitmap | undefined {
    const [entry, setEntry] = useState<{ source: TiledDrawingSource; bitmap: ImageBitmap }>();

    useEffect(() => {
        if (!source || maxSize === undefined) {
            return;
        }

        let cancelled = false;
        let bitmap: ImageBitmap | undefined;
        fetchPreviewBitmap(source, maxSize)
            .then((bmp) => {
                if (cancelled) {
                    bmp.close();
                    return;
                }
                bitmap = bmp;
                setEntry({ source, bitmap: bmp });
            })
            .catch((error) => {
                l.error("Failed to load tiled drawing preview:", error);
            });

        return () => {
            cancelled = true;
            if (bitmap) {
                bitmap.close();
            }
            setEntry((cur) => (cur?.bitmap === bitmap ? undefined : cur));
        };
    }, [source, maxSize]);

    // Only expose a bitmap belonging to the *current* source that hasn't been
    // released (a closed bitmap reports width 0 and would throw in drawImage).
    if (entry && entry.source === source && entry.bitmap.width > 0) {
        return entry.bitmap;
    }
    return undefined;
}
